import { useParams, Link } from "react-router-dom";
import { categories, categoryMap } from "@/mocks/categories";
import { getVideosByCategory } from "@/mocks/videos";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import ScrollToTop from "@/components/feature/ScrollToTop";
import NotFound from "@/pages/NotFound";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoryMap[slug] : undefined;

  if (!category) {
    return <NotFound />;
  }

  const videos = getVideosByCategory(slug!);

  const pageJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${category.name} - 宅男视频`,
    description: category.description,
    url: `https://zhainanvideo.com/category/${category.slug}`,
    isPartOf: {
      "@type": "WebSite",
      name: "宅男视频",
      url: "https://zhainanvideo.com",
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${category.name} - 宅男视频免费高清在线观看`}
        description={`${category.description}。宅男视频${category.name}专区，免费高清在线观看，播放流畅不卡顿，海量数字视频资源。`}
        keywords={`${category.name},宅男视频,91视频,97视频,免费高清,在线观看,数字视频平台`}
        canonicalUrl={`https://zhainanvideo.com/category/${category.slug}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageJsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="w-full px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: "首页", href: "/" },
                  { label: category.name },
                ]}
              />
            </div>

            {/* Category Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-xl">
                  <i className={`${category.icon} text-primary-600 text-lg`}></i>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">{category.name}</h1>
              </div>
              <p className="text-foreground-600 text-sm ml-13">{category.description}</p>
            </div>

            {/* Sub Categories Tabs */}
            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-1">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70"
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Videos Grid */}
            {videos.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                {videos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/category/${video.category}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-video rounded-xl overflow-hidden bg-background-200 mb-3">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        title={video.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                        loading="lazy"
                      />
                      <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded">
                        {video.duration}
                      </div>
                      {video.isHD && (
                        <div className="absolute top-2 left-2 bg-primary-500 text-white text-[10px] px-1.5 py-0.5 rounded font-semibold">
                          HD
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/90 opacity-0 group-hover:opacity-100 transition-all">
                          <i className="ri-play-fill text-foreground-950 text-xl"></i>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-950 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                      {video.title}
                    </h3>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {(video.views / 10000).toFixed(1)}万
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-amber-500"></i>
                        {video.rating}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center bg-background-100 rounded-2xl mx-auto mb-4">
                  <i className="ri-video-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类下暂无视频</p>
                <Link
                  to="/category/hot"
                  className="inline-flex items-center gap-1 mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer"
                >
                  去看看热门推荐 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            )}

            {/* Pagination */}
            {videos.length > 8 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-background-200 text-sm cursor-pointer">
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-sm font-medium">
                  1
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 text-sm cursor-pointer">
                  2
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-background-200 text-sm cursor-pointer">
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}